import {default as JsfAjax, JsfAjaxEvent, JsfAjaxResponseEvent} from "./JsfAjax";
import calcDistance from "../calcDistance";
import onDocumentLoaded from "../onDocumentLoaded";
import EventListenerManager from "../../components/EventListenerManager";
import deepCopy from "../deepCopy";

export type JsfAjaxScrollImprovementsOptions = {
	getScrollArea: () => Element | null,
	onUpdateIds: string[],
	selectedItemQuery?: string,
	preferCenteringSelectedIfSourceNotAncestor?: boolean,
	alwaysRememberScrollPosition?: boolean
};
const defaultOptions: Partial<JsfAjaxScrollImprovementsOptions> = {
	preferCenteringSelectedIfSourceNotAncestor: true,
	alwaysRememberScrollPosition: false
};


/**
 * Used when a component has a scrollable area which is ajax updated. This method will make it:
 * 1) Maintain the scroll position if the contents is the same after the ajax update (done by comparing "textContent")
 * 2) If a previous position is unknown, for example upon init, but there is a selected element, this will scroll to the first selected element.
 *
 * When the component you use this class in is removed/destroyed, call the destroy() on your instance of this.
 */
export default class JsfAjaxScrollImprovements {
	private static uidCount = 0;

	private readonly uid = JsfAjaxScrollImprovements.uidCount++;
	private readonly eventListenerManager = new EventListenerManager();
	private readonly options: JsfAjaxScrollImprovementsOptions;

	/**
	 * Adds the scroll improvements. See class description for more.
	 * Use an empty string (""), undefined or null for the `scrollAreaQuery` argument if the scroll area is the same as returned by the `getAjaxElement` argument.
	 * Use undefined or null for `selectedItemQuery` argument to disable scrolling to selected item.
	 * Returns an instance of JsfAjaxScrollImprovements which you pass to `remove()` to remove the enhancements.
	 *
	 * TODO @Michael: Update documentation here and throughout this file.
	 */
	public constructor(
		document: Document,
		options?: JsfAjaxScrollImprovementsOptions
	) {
		this.options = deepCopy([defaultOptions, options]);

		const ajaxListenerOptions = {updateId: this.options.onUpdateIds};
		const jsfAjaxManaged = this.eventListenerManager.manage(JsfAjax.getInstance());
		jsfAjaxManaged.addEventListener("responded", this.onAjaxResponded, ajaxListenerOptions);
		jsfAjaxManaged.addEventListener("success", this.onAjaxSuccess, ajaxListenerOptions);

		onDocumentLoaded(document, () => {
			this.updateScrollLocation();
		}, true);
	}

	public destroy() {
		this.eventListenerManager.removeListeners();
	}

	private storeScrollPosition(ev: JsfAjaxEvent, position: {top: number, left: number}) {
		if (ev.internalOptions.jsfAjaxScrollImprovements_scrollPositions === undefined) {
			ev.internalOptions.jsfAjaxScrollImprovements_scrollPositions = Object.create(null);
		}
		ev.internalOptions.jsfAjaxScrollImprovements_scrollPositions[this.uid] = position;
	}
	private getScrollPosition(ev: JsfAjaxEvent): {top: number, left: number} | undefined {
		if (ev.internalOptions.jsfAjaxScrollImprovements_scrollPositions === undefined) {
			return undefined;
		}
		const position = ev.internalOptions.jsfAjaxScrollImprovements_scrollPositions[this.uid];
		return position !== undefined && position.top !== undefined && position.left !== undefined
			? position as {top: number, left: number}
			: undefined;
	}


	private storeIsSourceAncestorOfScrollArea(ev: JsfAjaxEvent, isSourceAncestorOfScrollArea: boolean) {
		if (ev.internalOptions.jsfAjaxScrollImprovements_isSourceAncestorOfScrollArea === undefined) {
			ev.internalOptions.jsfAjaxScrollImprovements_isSourceAncestorOfScrollArea = Object.create(null);
		}
		ev.internalOptions.jsfAjaxScrollImprovements_isSourceAncestorOfScrollArea[this.uid] = isSourceAncestorOfScrollArea;
	}
	private isSourceAncestorOfScrollArea(ev: JsfAjaxEvent | undefined): boolean {
		if (ev === undefined || ev.internalOptions.jsfAjaxScrollImprovements_isSourceAncestorOfScrollArea === undefined) {
			return false;
		}
		return ev.internalOptions.jsfAjaxScrollImprovements_isSourceAncestorOfScrollArea[this.uid];
	}

	private static compareUpdateToCurrent(updateId: string, responseXML: Document, currentDocument: Document): boolean {
		// Determine if the HTML is for the "same" content.
		// We compare the textContent as a pretty good test as to if the display is the same while ignoring markup changes (e.g. styleClass changes).
		const updateElementUnparsed = responseXML.querySelector(`update[id="${updateId}"]`);
		const currentElement = currentDocument.getElementById(updateId);

		if (currentElement === null || updateElementUnparsed === null || updateElementUnparsed.firstChild === null) {
			return false;
		}

		// First, we need to parse the contents of the update to compare it
		// We wrap the node value in a <div/> when parsing because it may not have a root element.
		// We also remove all scripts (JSF may add some and we don't care if they are different for our purposes).
		const updateNodeValue = updateElementUnparsed.firstChild.nodeValue;
		const parser = new DOMParser();
		const parsedUpdateDocument = parser.parseFromString(`<div>${updateNodeValue}</div>`, "text/html");
		const scripts = parsedUpdateDocument.querySelectorAll("script");
		scripts.forEach((scriptElem: Element) => {
			scriptElem.remove();
		});
		const parsedUpdate = parsedUpdateDocument.firstElementChild;
		if (parsedUpdate === null) {
			return false; // This should never happen.
		}

		// Then we can compare
		return parsedUpdate.textContent === currentElement.textContent;
	}

	private readonly onAjaxResponded = (ev: JsfAjaxResponseEvent) => {
		if (ev.responseXML === undefined || ev.responseXML == null) {
			return;
		}

		const scrollArea = this.options.getScrollArea.call(this);
		if (scrollArea === null) {
			return;
		}

		// Store the current scroll position if we always
		if (this.options.alwaysRememberScrollPosition) {
			this.storeScrollPosition(ev, {top: scrollArea.scrollTop, left: scrollArea.scrollLeft});
			return;
		}

		// Check if the update to the area is the same content as what is currently in the document
		// Since we may be monitoring multiple updateIds we need to check each. All we want is
		// one to match and then we can move on.
		const currentDocument = scrollArea.ownerDocument;
		let isComparableContent = false;
		for (let i = 0; i < this.options.onUpdateIds.length; i++) {
			const updateId = this.options.onUpdateIds[i];
			if (updateId !== undefined && JsfAjaxScrollImprovements.compareUpdateToCurrent(updateId, ev.responseXML, currentDocument)) {
				isComparableContent = true;
				break;
			}
		}

		// If comparable content, we will store the current scroll location so it is maintained after the ajax
		// updated content is put in place
		if (isComparableContent) {
			this.storeScrollPosition(ev, {top: scrollArea.scrollTop, left: scrollArea.scrollLeft});
		}

		// Determine if the source element that triggered the AJAX element is an ancestor of the scrollArea or not.
		// If it is, that means the user clicked inside the scroll area and we should maybe set the scroll different
		// when the updated content is put in place
		const isSourceAncestorOfScrollArea =
			ev.sourceElement !== undefined && ev.sourceElement !== null
			&& (scrollArea.compareDocumentPosition(ev.sourceElement) & Node.DOCUMENT_POSITION_CONTAINED_BY) !== 0;
		this.storeIsSourceAncestorOfScrollArea(ev, isSourceAncestorOfScrollArea);
	}

	private readonly onAjaxSuccess = (ev: JsfAjaxResponseEvent) => {
		this.updateScrollLocation(ev);
	}

	private getSelectedElement(scrollAreaElem: Element): Element | null {
		if (this.options.selectedItemQuery !== undefined) {
			return scrollAreaElem.querySelector(this.options.selectedItemQuery);
		}
		return null;
	}


	private readonly updateScrollLocation = (ev?: JsfAjaxResponseEvent) => {
		const scrollArea = this.options.getScrollArea.call(this);
		if (scrollArea === null) {
			return;
		}

		// Restore last known position, if there is one (stored before AJAX updates are processed).
		if (ev !== undefined) {
			const lastScrollPosition = this.getScrollPosition(ev);
			if (lastScrollPosition !== undefined) {
				scrollArea.scrollTop = lastScrollPosition.top;
				scrollArea.scrollLeft = lastScrollPosition.left;
			}
		}

		// Scroll to selected item (if conditions are met)
		const selectedElement = this.getSelectedElement(scrollArea);
		if (selectedElement !== null) {
			// Wait a frame so the initial position renders and then we scroll ... Potentially.
			requestAnimationFrame(() => {
				const minEdgeSpacing = 10;
				const scrollAreaBR = scrollArea.getBoundingClientRect();
				const selectedBR = selectedElement.getBoundingClientRect();
				let left: number;
				let top: number;

				const calcPosition = (
					scrollValue: number,
					itemStart: number, itemEnd: number, itemSize: number,
					containerStart: number, containerEnd: number, containerSize: number
				) => {
					containerStart += minEdgeSpacing;
					containerEnd -= minEdgeSpacing;
					containerSize = containerSize - (minEdgeSpacing * 2);

					// Calculate position to scroll:
					// First we calculate the percentage of the item that is visible (over the total possible; e.g. think where the item is larger than the container, we want that to be 100%)
					// If the item is not fully visible, we scroll to the have the top of the item against the top of the container,
					// unless the bottom edge is closer, than we use that. Unless, the AJAX change was cause by something external to the scroll area,
					// in which case we are going to prefer centering because that will make it more visible.
					// In other words, if the user clicks an item we just move it fully in to view without moving more than required. But if the AJAX render was caused by something else (not a user
					// interacting with the content in the scroll area) then we will scroll so the item is centered (if possible).
					// Finally note, if the item is larger than the container, we won't center it.
					let value = scrollValue;
					let visiblePercent = 0;
					if (itemStart < containerEnd && itemEnd > containerStart) {
						const visibleAmount = Math.min(itemEnd, containerEnd) - Math.max(itemStart, containerStart);
						visiblePercent = visibleAmount / Math.min(itemSize, containerSize);
					}
					if (visiblePercent < 1) {
						value = (itemStart - containerStart) + scrollValue; // Start edge
						if (itemSize <= containerSize) {
							if (this.options.preferCenteringSelectedIfSourceNotAncestor && !this.isSourceAncestorOfScrollArea(ev)) {
								value = value - (containerSize - itemSize) / 2; // Center
							} else {
								let valueEnd = (itemEnd - containerEnd) + scrollValue; // End edge
								if (Math.abs(scrollValue - valueEnd) < Math.abs(scrollValue - value)) {
									// Use if the valueEnd is closer than the start value (the top or left value)
									value = valueEnd;
								}
							}
						}
					}
					return Math.round(value);
				}

				left = calcPosition(
					scrollArea.scrollLeft,
					selectedBR.left, selectedBR.right, selectedBR.width,
					scrollAreaBR.left, scrollAreaBR.right, scrollAreaBR.width
				);
				top = calcPosition(
					scrollArea.scrollTop,
					selectedBR.top, selectedBR.bottom, selectedBR.height,
					scrollAreaBR.top, scrollAreaBR.bottom, scrollAreaBR.height
				);

				left = left < 0 ? 0 : Math.min(left, scrollArea.scrollWidth - scrollArea.clientWidth);
				top =  top < 0 ? 0 : Math.min(top, scrollArea.scrollHeight - scrollArea.clientHeight);

				const distance = calcDistance(scrollArea.scrollLeft, scrollArea.scrollTop, left, top);
				const duration = Math.max(50, Math.min(1250, distance * 1.5));
				const easing = duration < 175 ? "linear" : "easeOutCubic";

				if (ev === undefined) { // If no event object this is during page load and we don't want to animate.
					scrollArea.scrollTop = top;
					scrollArea.scrollLeft = left;
				} else {
					// Using JQuery because it is just faster to put in place. Can't rely on scrollIntoView because not
					// all browsers support "smooth" -- only have it here as a fallback since I don't like to rely on
					// JQuery being loaded (even though it will be)
					const $ = (window as any).jQuery;
					if ($ !== undefined) {
						const $scrollAreaElem = $(scrollArea);
						$scrollAreaElem.stop(true).animate({
							scrollTop: top,
							scrollLeft: left
						}, duration, easing);
					} else {
						// TODO (Michael) Change to scroll to the position calculated above.
						selectedElement.scrollIntoView({
							behavior: "smooth",
							block: "center",
							inline: "center"
						});
					}
				}
			});
		}
	}
}
